/**
 * AuditLogViewer — Platform Console panel.
 * Filterable, paginated view of governance/security events. Root-only.
 */
import React, { useEffect, useState, useCallback } from 'react';
import GlassSelect from './GlassSelect';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE = 25;

const QUICK_FILTERS = [
  { label: 'Last 24h',      preset: () => ({ since: _isoDaysAgo(1) }) },
  { label: 'Last 7d',       preset: () => ({ since: _isoDaysAgo(7) }) },
  { label: 'MFA events',    preset: () => ({ action_prefix: 'mfa' }) },
  { label: 'Session revoke',preset: () => ({ action_prefix: 'session' }) },
  { label: 'Rate limits',   preset: () => ({ action_prefix: 'platform.rate_limit' }) },
  { label: 'SSO config',    preset: () => ({ action_prefix: 'sso' }) },
  { label: 'Retention',     preset: () => ({ action_prefix: 'platform.retention' }) },
];

function _isoDaysAgo(days) {
  const d = new Date(Date.now() - days * 86400_000);
  return d.toISOString();
}

function Pill({ tone, children }) {
  const colors = {
    platform: { bg: '#dbeafe', color: '#1e3a8a' },
    tenant:   { bg: '#dcfce7', color: '#166534' },
    auth:     { bg: '#ede9fe', color: '#5b21b6' },
    governance: { bg: '#fef3c7', color: '#92400e' },
    default:  { bg: '#f3f4f6', color: '#4b5563' },
  };
  const c = colors[tone] || colors.default;
  return <span style={{
    fontSize: 10, padding: '2px 6px', borderRadius: 10, textTransform: 'uppercase',
    letterSpacing: '0.05em', background: c.bg, color: c.color, fontWeight: 600,
  }}>{children}</span>;
}

function ActionCategoryPill({ action }) {
  if (!action) return null;
  const a = action.toLowerCase();
  if (a.startsWith('mfa') || a.includes('.mfa')) return <Pill tone="auth">MFA</Pill>;
  if (a.startsWith('session')) return <Pill tone="auth">SESSION</Pill>;
  if (a.startsWith('platform.rate_limit')) return <Pill tone="governance">RATE-LIMIT</Pill>;
  if (a.startsWith('platform.retention')) return <Pill tone="governance">RETENTION</Pill>;
  if (a.startsWith('sso')) return <Pill tone="governance">SSO</Pill>;
  if (a.startsWith('platform')) return <Pill tone="platform">PLATFORM</Pill>;
  if (a.startsWith('integration')) return <Pill tone="governance">INTEGRATION</Pill>;
  if (a.startsWith('user') || a.startsWith('tenant')) return <Pill tone="tenant">{a.split('.')[0]}</Pill>;
  return <Pill tone="default">{a.split('.')[0]}</Pill>;
}

function DiffPreview({ oldV, newV }) {
  // Render a compact old→new summary. Both may be primitives, dicts, or undefined.
  if (oldV === undefined && newV === undefined) return null;
  const render = (v) => {
    if (v === null || v === undefined) return <em style={{ color: 'var(--text-secondary)' }}>—</em>;
    if (typeof v === 'object') {
      const entries = Object.entries(v).slice(0, 5);
      return <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>
        {entries.map(([k, val]) => `${k}=${JSON.stringify(val)}`).join(' ')}
      </span>;
    }
    return <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>{String(v)}</span>;
  };
  return (
    <div style={{ fontSize: 11, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      {oldV !== undefined && <>{render(oldV)}<span style={{ color: 'var(--text-secondary)' }}>→</span></>}
      {render(newV)}
    </div>
  );
}

export default function AuditLogViewer() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [knownActions, setKnownActions] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    actor_email: '', action: '', action_prefix: '',
    scope: '', tenant_id: '', target_id: '',
    since: '', until: '',
  });

  const load = useCallback(async (nextOffset = 0, next = filters) => {
    setLoading(true); setError(null);
    const params = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => { if (v) params.set(k, v); });
    params.set('limit', PAGE);
    params.set('offset', nextOffset);
    try {
      const r = await fetch(`${API}/api/audit/platform/logs?${params}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setLogs(d.logs || []);
      setTotal(d.total || 0);
      setOffset(nextOffset);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(0); (async () => {
    try {
      const r = await fetch(`${API}/api/audit/platform/logs/actions`);
      if (r.ok) setKnownActions((await r.json()).actions || []);
    } catch { /* ignore */ }
  })(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const applyPreset = (preset) => {
    const next = { ...filters, ...preset() };
    // Mutually-exclusive safety: only keep the preset's key in action/action_prefix
    if (preset().action_prefix) next.action = '';
    if (preset().action) next.action_prefix = '';
    setFilters(next);
    load(0, next);
  };

  const apply = () => load(0);
  const clear = () => {
    const empty = { actor_email: '', action: '', action_prefix: '', scope: '',
                    tenant_id: '', target_id: '', since: '', until: '' };
    setFilters(empty); load(0, empty);
  };

  return (
    <div className="panel" style={{ marginBottom: 12 }} data-testid="audit-log-viewer">
      <div className="panel-header">Audit log</div>
      <div className="panel-body">
        {/* Quick filters */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {QUICK_FILTERS.map((qf) => (
            <button key={qf.label} className="btn btn-outline btn-sm"
                    onClick={() => applyPreset(qf.preset)}
                    data-testid={`audit-preset-${qf.label.replace(/\s+/g, '-').toLowerCase()}`}>
              {qf.label}
            </button>
          ))}
          <button className="btn btn-outline btn-sm" onClick={clear}
                  data-testid="audit-clear-filters">Clear</button>
        </div>

        {/* Fine filters */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 8 }}>
          <input placeholder="Actor email contains…" value={filters.actor_email}
                 onChange={(e) => setFilters({ ...filters, actor_email: e.target.value })}
                 data-testid="audit-filter-actor-email"
                 style={{ padding: 6, border: '1px solid var(--border)', borderRadius: 4, fontSize: 12 }} />
          <GlassSelect value={filters.action}
                  onChange={(e) => setFilters({ ...filters, action: e.target.value, action_prefix: '' })}
                  data-testid="audit-filter-action"
                  style={{ padding: 6, border: '1px solid var(--border)', borderRadius: 4, fontSize: 12 }}>
            <option value="">Any action</option>
            {knownActions.map((a) => <option key={a} value={a}>{a}</option>)}
          </GlassSelect>
          <GlassSelect value={filters.scope}
                  onChange={(e) => setFilters({ ...filters, scope: e.target.value })}
                  data-testid="audit-filter-scope"
                  style={{ padding: 6, border: '1px solid var(--border)', borderRadius: 4, fontSize: 12 }}>
            <option value="">Any scope</option>
            <option value="platform">platform</option>
            <option value="tenant">tenant</option>
          </GlassSelect>
          <input placeholder="Tenant id" value={filters.tenant_id}
                 onChange={(e) => setFilters({ ...filters, tenant_id: e.target.value })}
                 data-testid="audit-filter-tenant"
                 style={{ padding: 6, border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, fontFamily: "'IBM Plex Mono', monospace" }} />
          <input placeholder="Target id" value={filters.target_id}
                 onChange={(e) => setFilters({ ...filters, target_id: e.target.value })}
                 data-testid="audit-filter-target"
                 style={{ padding: 6, border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, fontFamily: "'IBM Plex Mono', monospace" }} />
          <input type="datetime-local" value={filters.since.slice(0, 16)}
                 onChange={(e) => setFilters({ ...filters, since: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                 data-testid="audit-filter-since"
                 style={{ padding: 6, border: '1px solid var(--border)', borderRadius: 4, fontSize: 12 }} />
          <input type="datetime-local" value={filters.until.slice(0, 16)}
                 onChange={(e) => setFilters({ ...filters, until: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                 data-testid="audit-filter-until"
                 style={{ padding: 6, border: '1px solid var(--border)', borderRadius: 4, fontSize: 12 }} />
          <button className="btn btn-primary btn-sm" onClick={apply}
                  data-testid="audit-apply-filters">Apply</button>
        </div>

        {error && <div className="auth-error" data-testid="audit-error">{error}</div>}
        {loading && <div style={{ fontSize: 12 }}>Loading audit log…</div>}

        {!loading && (
          <>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
              Showing {logs.length} of {total.toLocaleString()} matching events.
            </div>
            <div className="data-table-wrap">
              <table className="data-table" style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap' }}>Timestamp</th>
                    <th>Actor</th>
                    <th>Action</th>
                    <th>Scope</th>
                    <th>Tenant</th>
                    <th>Target</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const rowId = log.id || `${log.timestamp}-${log.action}`;
                    const exp = !!expanded[rowId];
                    return (
                      <React.Fragment key={rowId}>
                        <tr data-testid={`audit-row-${rowId}`}
                            onClick={() => setExpanded({ ...expanded, [rowId]: !exp })}
                            style={{ cursor: 'pointer' }}>
                          <td style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", whiteSpace: 'nowrap' }}>
                            {new Date(log.timestamp).toLocaleString()}
                          </td>
                          <td style={{ fontSize: 12 }}>
                            {log.actor_email || log.actor_id || '—'}
                            {log.actor_is_platform_root && <span style={{ fontSize: 9, marginLeft: 4, color: 'var(--accent)' }}>ROOT</span>}
                          </td>
                          <td style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <ActionCategoryPill action={log.action} />
                            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>{log.action}</span>
                          </td>
                          <td>
                            {(log.metadata?.scope || log.scope) && <Pill tone={log.metadata?.scope}>{log.metadata?.scope}</Pill>}
                          </td>
                          <td style={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", color: 'var(--text-secondary)' }}>
                            {log.tenant_id ? `${log.tenant_id.slice(0, 8)}…` : '—'}
                          </td>
                          <td style={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", color: 'var(--text-secondary)' }}>
                            {log.target_type ? `${log.target_type}:${(log.target_id || '').slice(0, 8)}…` : '—'}
                          </td>
                          <td>
                            <DiffPreview
                              oldV={log.metadata?.old_value}
                              newV={log.metadata?.new_value} />
                          </td>
                        </tr>
                        {exp && (
                          <tr>
                            <td colSpan={7} style={{ background: 'var(--bg)', padding: 8 }}>
                              <pre style={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", margin: 0, whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(log, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {logs.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 16, color: 'var(--text-secondary)', fontSize: 12 }}>
                      No events match these filters.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
              <button className="btn btn-outline btn-sm" disabled={offset === 0}
                      onClick={() => load(Math.max(0, offset - PAGE))}
                      data-testid="audit-prev-btn">← Prev</button>
              <button className="btn btn-outline btn-sm" disabled={offset + PAGE >= total}
                      onClick={() => load(offset + PAGE)}
                      data-testid="audit-next-btn">Next →</button>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
